import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { SummaryDiagnosticService } from '../summary-diagnostic.service';
import { DetailDiagViewComponent } from './detail-diag-view/detail-diag-view.component';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-data-table',
    templateUrl: './data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DataTableComponent implements OnInit, OnChanges, OnDestroy {
  private readonly subs = new Subscription();

  constructor(
    public summaryDiagService: SummaryDiagnosticService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  @Input('table-sheet')
  tableSheet: any;
  expand: boolean = true;
  checked: boolean = false;
  color = 'accent';
  scrollBarHidden: boolean = false;
  @ViewChild(DetailDiagViewComponent) detailModal!: DetailDiagViewComponent;
  showAddEditModalViewer: boolean = false;
  drillDownObj: any;
  @Input('index')
  index: any;

  @Input('searchValue')
  searchValue: any;

  @Input('kpiFilter')
  kpiFilter: any;

  @Input('hideResolvedDiag')
  hideResolvedDiag: boolean = false;

  @Input('expandCollapseAll')
  expandCollapseAll: boolean = false;

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '/gtw',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: '',
  };
  globalParams: any = {};
  GENERAL_CONFIG: any = {};

  @Output() loadDashboardScreen = new EventEmitter<any>();

  cols: any[] = [
    { header: 'Diagnostic Description', field: 'DIAG_DESC' },
    { header: 'Not Loaded/Failed', field: 'FAILCNT' },
    { header: 'System', field: 'DIAG_SOURCE_SYSTEM_FLAG' },
    { header: ' Criticality/Priority', field: 'DIAG_MSG' },
  ];

  get tableColumns(): any[] {
    return this.tableSheet?.[0]?.extraOptionsJSON?.componentSettings?.tableOptions?.columns || [];
  }

  get visibleTableColumns(): { col: any; id: string; index: number }[] {
    return this.tableColumns
      .map((col: any, index: number) => ({ col, id: this.getColumnId(col, index), index }))
      .filter(({ col }) => col.data === null || col.visible);
  }

  get displayedColumnIds(): string[] {
    return this.visibleTableColumns.map(entry => entry.id);
  }

  getColumnId(col: any, index: number): string {
    return col?.data != null ? String(col.data) : `action_${index}`;
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.cdr.markForCheck();
      })
    );

    // The legacy table `[loading]="!tableSheet[0].sheet.isLoaded"`
    // binds against a field that the service mutates in place when each
    // sheet API resolves. With `ChangeDetectionStrategy.OnPush` and a
    // stable `[table-sheet]` input reference, the child is NEVER re-checked
    // after the initial render — so the loading overlay would stay on
    // forever even though the parent already flipped `sheet.isLoaded=true`.
    // Subscribe to `sheetsLoaded` here so this component marks itself
    // dirty whenever any sheet finishes; the next CD pass re-evaluates
    // `[loading]` and drops the spinner.
    this.subs.add(
      this.summaryDiagService.sheetsLoaded.subscribe(() => {
        this.cdr.markForCheck();
      })
    );

    console.log('data component', this.tableSheet);
    console.log('Index', this.index);

    if (this.summaryDiagService.dashboardPool[0]['hideResolvedDiag']) {
      this.hideResolvedDiag = this.summaryDiagService.dashboardPool[0]['hideResolvedDiag'];
      this.toggle();
    }
    if (this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['expanded'] != undefined) {
      this.expand = this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['expanded'];
    }
    if (this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['kpiFilter'] != undefined) {
      this.kpiFilter = this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['kpiFilter'];
      this.filterTableData();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalParams = header.globalParams || {};
    this.cdr.markForCheck();
  }

  private get selectedFilterParams(): any {
    return this.summaryDiagService.dashboardPool[0]?.['selectedFilterParams'];
  }

  visibleKeysArray: any = [];
  ngOnChanges(changes: SimpleChanges): void {
    console.log('filter changes', changes);
    if (changes.searchValue && changes.searchValue.currentValue) {
      const searchQuery =
        typeof changes.searchValue.currentValue === 'string'
          ? changes.searchValue.currentValue
          : changes.searchValue.currentValue.query;

      if (searchQuery && searchQuery !== '') {
        this.tableSheet[0].sheet.data = this.tableSheet[0].sheet.safeCopyArr.filter((o: any) =>
          Object.values(o).some((v: any) => _.includes(v?.toString()?.toLowerCase(), searchQuery.toLowerCase()))
        );
      } else {
        this.tableSheet[0].sheet.data = _.cloneDeep(this.tableSheet[0].sheet.safeCopyArr);
      }
    } else {
      this.tableSheet[0].sheet.data = _.cloneDeep(this.tableSheet[0].sheet.safeCopyArr);
    }
    let searchaValue = this.searchValue;
    let kpiFilter = this.kpiFilter;
    if ((changes && changes.hideResolvedDiag) || this.summaryDiagService.dashboardPool[0]['hideResolvedDiag']) {
      this.hideResolvedDiag = changes.hideResolvedDiag
        ? changes.hideResolvedDiag.currentValue
        : this.summaryDiagService.dashboardPool[0]['hideResolvedDiag'];
      this.toggle();
    }
    if ((changes && changes.expandCollapseAll) || this.summaryDiagService.dashboardPool[0]['expandCollapseAll'] != undefined) {
      this.expand = changes.expandCollapseAll ? changes.expandCollapseAll.currentValue : this.summaryDiagService.dashboardPool[0]['expandCollapseAll'];
      this.expandClose();
    }
    this.filterTableData();
    this.cdr.markForCheck();
  }
  filterTableData() {
    if (this.kpiFilter != 'All' && this.kpiFilter != '') {
      this.tableSheet[0].sheet.data = this.tableSheet[0].sheet.data.filter((o: any) => {
        return o.DIAG_MSG == this.kpiFilter;
      });
    }
    this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['kpiFilter'] = this.kpiFilter;
  }
  toggle() {
    console.log('toggle checked', event);
    console.log('data', this.tableSheet);
    if (this.hideResolvedDiag) {
      this.tableSheet[0].sheet.data = _.filter(this.tableSheet[0].sheet.data, function (i) {
        return i['FAILCNT'] > 0;
      });
    } else {
      this.tableSheet[0].sheet.data = this.tableSheet[0].sheet.safeCopyArr;
    }
    this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['isToggled'] = this.checked;
    this.filterTableData();
  }
  expandClose() {
    this.expand = !this.expand;
    this.summaryDiagService.dashboardPool[0]['components'][this.index][0]['expanded'] = this.expand;
    console.log('expand', this.expand);
  }
  changed() {
    console.log(this.checked);
  }
  showEditComponent(tableData: any, base_url: any) {
    this.showAddEditModalViewer = true;
    let tableConfigData = this.summaryDiagService.dashboardPool[0]['components'][this.index][0];
    setTimeout(() => {
      this.detailModal.showMore(
        'show-deatil-diag',
        tableConfigData,
        tableData,
        base_url,
        this.selectedFilterParams,
        this.globalParams
      );
    });
  }
  afterModalClosed() {
    this.showAddEditModalViewer = false;
  }
  gridFilters: any = {
    gridFilters: [],
    drillDownRefresh: 0,
    filterObj: {},
    screenFilter: [],
  };
  redirectDrillDown(rowData: any, i: any) {
    // Mirror legacy `detailedDiagnosticController.js` `case 'drillDown'`:
    // parse the column's drilldown JSON, hydrate `screenFilter[*].value` from
    // the active dashboard filters, then bubble a payload that exposes
    // `activity_key`, `screen_key`, `screenFilter`, and `gridFilters` at the
    // top level. The AngularJS bridge in `GlobalControllers.js` (handler for
    // the `load-detail-dashboard` custom event) reads those keys directly via
    // `workspaceFactory.loadLink('drillDown', '{}', screenObj.activity_key,
    // screenObj.screen_key, screenObj.screenFilter, screenObj.gridFilters)`,
    // so anything nested inside `screenFilter` is invisible to it.
    const drilldownRaw =
      this.tableSheet?.[0]?.extraOptionsJSON?.componentSettings?.drilldownFilterJson;
    if (!drilldownRaw) {
      return;
    }
    const drilldownObj = JSON.parse(drilldownRaw);

    // Legacy uses singular `screenFilter` everywhere; the previous code
    // iterated `obj.screenFilters` (plural) which silently never matched.
    if (Array.isArray(drilldownObj?.screenFilter)) {
      drilldownObj.screenFilter.forEach((sf: any) => {
        if (sf?.name) {
          sf.value = this.selectedFilterParams?.[sf.name];
        }
      });
    }

    // Build a fresh payload per click so consecutive drilldowns don't reuse
    // mutated state from prior calls (legacy declares the local `gridFilters`
    // object inside each handler invocation for the same reason).
    const payload: any = {
      ...drilldownObj,
      gridFilters: Array.isArray(drilldownObj?.gridFilters) ? drilldownObj.gridFilters : [],
      drillDownRefresh: drilldownObj?.drillDownRefresh ?? 0,
      filterObj: drilldownObj?.filterObj ?? {},
      filterParams: this.selectedFilterParams,
    };

    this.drillDownObj = drilldownObj;
    this.gridFilters = payload;
    this.summaryDiagService.drillDown.isDrillDown = true;
    this.summaryDiagService.drillDown.selectedFilterParms = this.selectedFilterParams;

    this.loadDashboardScreen.emit(payload);
  }

  loadDrillDownScreen(event: any) {
    this.loadDashboardScreen.emit(event);
  }
}
